import React, { useState } from 'react';
import Footer from '../../../components/Footer';
import VegIcon from '../../../components/VegIcon';
import './MenuExplorer.css';

import DEFAULT_LOGO from '../../../assets/defaults/logo-default.jpg';
import DEFAULT_BG from '../../../assets/defaults/bg-default.jpg';
import DEFAULT_ITEM from '../../../assets/defaults/item-default.jpg';
import MenuItemImage from '../../../components/MenuItemImage';
import RestaurantLogo from '../../../components/RestaurantLogo';

const MenuExplorer = ({ data, isSingleMenu }) => {
    const restaurant = data?.restaurant || {};
    const menus = data?.menus || [];
    const offers = data?.offers || [];

    // normalize offer type: API may return an enum object { name, value, displayName }
    const normalizeType = (offer) => {
        if (!offer || offer.type === undefined || offer.type === null) return '';
        if (typeof offer.type === 'string') return offer.type;
        return (offer.type.name || offer.type.displayName || '').toString();
    };

    // separate offers into banner (carousel) and notes (filtering is done on API side)
    const bannerOffers = offers.filter(o => normalizeType(o).toLowerCase() !== 'notes');
    const notesOffers = offers.filter(o => normalizeType(o).toLowerCase() === 'notes');
    const [bannerIndex, setBannerIndex] = React.useState(0);

    // Search state: query and computed results
    const [searchQuery, setSearchQuery] = React.useState('');

    const searchResults = React.useMemo(() => {
        const q = (searchQuery || '').trim().toLowerCase();
        if (!q) return { items: [], categories: [], menus: [] };

        const foundItems = [];
        const foundCategories = [];
        const foundMenus = [];

        (menus || []).forEach(menu => {
            const menuName = (menu.name || '').toLowerCase();
            const menuDesc = (menu.description || '').toLowerCase();
            if (menuName.includes(q) || menuDesc.includes(q)) {
                foundMenus.push(menu);
            }

            (menu.categories || []).forEach(cat => {
                const catName = (cat.name || '').toLowerCase();
                const catDesc = (cat.description || '').toLowerCase();
                if (catName.includes(q) || catDesc.includes(q)) {
                    foundCategories.push({ menu, category: cat });
                }

                (cat.items || []).forEach(item => {
                    const hay = `${item.name || ''} ${item.description || ''} ${(item.tags || []).join(' ')}`.toLowerCase();
                    if (hay.includes(q)) {
                        foundItems.push({ menu, category: cat, item });
                    }
                });
            });
        });

        return { items: foundItems, categories: foundCategories, menus: foundMenus };
    }, [searchQuery, menus]);

    // Touch/drag state for carousel interaction
    const [touchStart, setTouchStart] = React.useState(null);
    const [touchEnd, setTouchEnd] = React.useState(null);
    const [isDragging, setIsDragging] = React.useState(false);
    const [isPaused, setIsPaused] = React.useState(false);
    const [showNotesModal, setShowNotesModal] = React.useState(false);
    const [currentNoteIndex, setCurrentNoteIndex] = React.useState(0);
    const [isNotesPaused, setIsNotesPaused] = React.useState(false);
    const [notesTouchStart, setNotesTouchStart] = React.useState(null);
    const [notesTouchEnd, setNotesTouchEnd] = React.useState(null);
    const [isNotesDragging, setIsNotesDragging] = React.useState(false);

    // Minimum swipe distance (in px) to trigger slide change
    const minSwipeDistance = 50;

    // Helper function to format note text with line breaks and tabs
    const formatNoteText = (text) => {
        if (!text) return null;

        // Split by newlines (handle both \n and \r\n)
        const lines = text.split(/\r?\n/);

        return lines.map((line, index) => {
            // Replace tabs with 4 spaces for indentation
            const formattedLine = line.replace(/\t/g, '\u00A0\u00A0\u00A0\u00A0');

            return (
                <React.Fragment key={index}>
                    {formattedLine}
                    {index < lines.length - 1 && <br />}
                </React.Fragment>
            );
        });
    };

    // auto-advance carousel every 5s (pause on interaction)
    React.useEffect(() => {
        if (!bannerOffers || bannerOffers.length <= 1 || isPaused) return;
        const t = setInterval(() => {
            setBannerIndex(i => (i + 1) % bannerOffers.length);
        }, 5000);
        return () => clearInterval(t);
    }, [bannerOffers.length, isPaused]);

    // Touch handlers for mobile swipe
    const onTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
        setIsPaused(true);
    };

    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe && bannerOffers.length > 0) {
            setBannerIndex((i) => (i + 1) % bannerOffers.length);
        }
        if (isRightSwipe && bannerOffers.length > 0) {
            setBannerIndex((i) => (i - 1 + bannerOffers.length) % bannerOffers.length);
        }

        // Resume auto-advance after 3 seconds
        setTimeout(() => setIsPaused(false), 3000);
    };

    // Mouse drag handlers for desktop
    const onMouseDown = (e) => {
        setIsDragging(true);
        setTouchStart(e.clientX);
        setTouchEnd(null);
        setIsPaused(true);
    };

    const onMouseMove = (e) => {
        if (!isDragging) return;
        setTouchEnd(e.clientX);
    };

    const onMouseUp = () => {
        if (!isDragging || !touchStart || touchEnd === null) {
            setIsDragging(false);
            return;
        }

        const distance = touchStart - touchEnd;
        const isLeftDrag = distance > minSwipeDistance;
        const isRightDrag = distance < -minSwipeDistance;

        if (isLeftDrag && bannerOffers.length > 0) {
            setBannerIndex((i) => (i + 1) % bannerOffers.length);
        }
        if (isRightDrag && bannerOffers.length > 0) {
            setBannerIndex((i) => (i - 1 + bannerOffers.length) % bannerOffers.length);
        }

        setIsDragging(false);
        setTouchStart(null);
        setTouchEnd(null);

        // Resume auto-advance after 3 seconds
        setTimeout(() => setIsPaused(false), 3000);
    };

    const onMouseLeave = () => {
        if (isDragging) {
            setIsDragging(false);
            setTouchStart(null);
            setTouchEnd(null);
            setTimeout(() => setIsPaused(false), 3000);
        }
    };

    // Keyboard navigation
    React.useEffect(() => {
        const handleKeyDown = (e) => {
            if (bannerOffers.length <= 1) return;
            if (e.key === 'ArrowLeft') {
                setBannerIndex((i) => (i - 1 + bannerOffers.length) % bannerOffers.length);
            } else if (e.key === 'ArrowRight') {
                setBannerIndex((i) => (i + 1) % bannerOffers.length);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [bannerOffers.length]);

    // Keyboard navigation for notes modal
    React.useEffect(() => {
        if (!showNotesModal || notesOffers.length <= 1) return;

        const handleKeyDown = (e) => {
            if (e.key === 'ArrowLeft') {
                setCurrentNoteIndex((i) => (i - 1 + notesOffers.length) % notesOffers.length);
            } else if (e.key === 'ArrowRight') {
                setCurrentNoteIndex((i) => (i + 1) % notesOffers.length);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [showNotesModal, notesOffers.length]);

    // Auto-advance notes carousel every 5s (pause on interaction)
    React.useEffect(() => {
        if (!showNotesModal || !notesOffers || notesOffers.length <= 1 || isNotesPaused) return;
        const t = setInterval(() => {
            setCurrentNoteIndex(i => (i + 1) % notesOffers.length);
        }, 5000);
        return () => clearInterval(t);
    }, [showNotesModal, notesOffers.length, isNotesPaused]);

    // Touch handlers for notes modal swipe
    const onNotesTouchStart = (e) => {
        setNotesTouchEnd(null);
        setNotesTouchStart(e.targetTouches[0].clientX);
        setIsNotesPaused(true);
    };

    const onNotesTouchMove = (e) => {
        setNotesTouchEnd(e.targetTouches[0].clientX);
    };

    const onNotesTouchEnd = () => {
        if (!notesTouchStart || !notesTouchEnd) return;
        const distance = notesTouchStart - notesTouchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe && notesOffers.length > 0) {
            setCurrentNoteIndex((i) => (i + 1) % notesOffers.length);
        }
        if (isRightSwipe && notesOffers.length > 0) {
            setCurrentNoteIndex((i) => (i - 1 + notesOffers.length) % notesOffers.length);
        }

        // Resume auto-advance after 3 seconds
        setTimeout(() => setIsNotesPaused(false), 3000);
    };

    // Mouse drag handlers for notes modal desktop
    const onNotesMouseDown = (e) => {
        setIsNotesDragging(true);
        setNotesTouchStart(e.clientX);
        setNotesTouchEnd(null);
        setIsNotesPaused(true);
    };

    const onNotesMouseMove = (e) => {
        if (!isNotesDragging) return;
        setNotesTouchEnd(e.clientX);
    };

    const onNotesMouseUp = () => {
        if (!isNotesDragging || !notesTouchStart || notesTouchEnd === null) {
            setIsNotesDragging(false);
            return;
        }

        const distance = notesTouchStart - notesTouchEnd;
        const isLeftDrag = distance > minSwipeDistance;
        const isRightDrag = distance < -minSwipeDistance;

        if (isLeftDrag && notesOffers.length > 0) {
            setCurrentNoteIndex((i) => (i + 1) % notesOffers.length);
        }
        if (isRightDrag && notesOffers.length > 0) {
            setCurrentNoteIndex((i) => (i - 1 + notesOffers.length) % notesOffers.length);
        }

        setIsNotesDragging(false);

        // Resume auto-advance after 3 seconds
        setTimeout(() => setIsNotesPaused(false), 3000);
    };

    const onNotesMouseLeave = () => {
        if (isNotesDragging) {
            setIsNotesDragging(false);
            setTimeout(() => setIsNotesPaused(false), 3000);
        }
    };

    // current view state
    const [stage, setStage] = useState(isSingleMenu ? 'categories' : 'menus');
    const [activeMenu, setActiveMenu] = useState(isSingleMenu ? menus[0] : null);
    const [activeCategory, setActiveCategory] = useState(null);
    const [activeItem, setActiveItem] = useState(null);
    const [showVariantsModal, setShowVariantsModal] = useState(false);

    // history stack to emulate app-like back navigation
    // each entry stores a snapshot of navigation-related state
    const [historyStack, setHistoryStack] = useState([]);
    const historyStackRef = React.useRef([]);

    // mark current browser entry as in-app root on mount and listen for back navigation
    React.useEffect(() => {
        // mark current entry so popstate events can be recognized
        if (window && window.history && window.history.replaceState) {
            window.history.replaceState({ meExplorer: true }, '');
        }

        const onPopState = (e) => {
            // if this popstate belongs to our app, restore from internal stack
            if (e.state && e.state.meExplorer) {
                const stack = historyStackRef.current || [];
                if (stack.length > 0) {
                    const last = stack.pop();
                    historyStackRef.current = stack;
                    setHistoryStack([...stack]);

                    // restore snapshot
                    setStage(last.stage);
                    setActiveMenu(last.activeMenu);
                    setActiveCategory(last.activeCategory);
                    setActiveItem(last.activeItem);
                    setShowVariantsModal(!!last.showVariantsModal);
                    window.scrollTo(0, 0);
                } else {
                    // no internal snapshot left — restore root
                    setStage(isSingleMenu ? 'categories' : 'menus');
                    setActiveMenu(isSingleMenu ? menus[0] : null);
                    setActiveCategory(null);
                    setActiveItem(null);
                    setShowVariantsModal(false);
                    historyStackRef.current = [];
                    setHistoryStack([]);
                }
            }
            // if not our state, let browser handle navigation (page leave)
        };

        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, [isSingleMenu, menus]);

    const pushHistory = () => {
        const snapshot = { stage, activeMenu, activeCategory, activeItem, showVariantsModal };
        const newStack = [...(historyStackRef.current || []), snapshot];
        historyStackRef.current = newStack;
        setHistoryStack(newStack);
        // add a browser history entry so Back triggers popstate
        if (window && window.history && window.history.pushState) {
            window.history.pushState({ meExplorer: true }, '');
        }
    };

    const goBack = () => {
        // If there is an active search, clear it first and stay on the page
        if (searchQuery && (searchQuery || '').trim() !== '') {
            setSearchQuery('');
            return;
        }

        // Otherwise trigger browser back — popstate handler will restore component state
        if (window && window.history && window.history.length > 0) {
            window.history.back();
        }
    };

    const openMenu = (menu) => {
        pushHistory();
        setActiveMenu(menu);
        setActiveCategory(null);
        setStage('categories');
        window.scrollTo(0, 0);
    };

    const openCategory = (cat) => {
        pushHistory();
        setActiveCategory(cat);
        setStage('items');
        window.scrollTo(0, 0);
    };

    const openItem = (item) => {
        pushHistory();
        setActiveItem(item);
        setShowVariantsModal(true);
    };

    if (!data) return <div className="me-loading">Loading...</div>;

    const bgImage = restaurant.backgroundImageUrl || DEFAULT_BG;
    const headerStyle = { backgroundImage: `linear-gradient(rgba(0,0,0,0.30), rgba(0,0,0,0.30)), url(${bgImage})` };
    const headerClass = `me-header me-header-bg`;

    return (
        <div className="me-root">

            {/* Header with restaurant info */}
            <header className={headerClass} style={headerStyle}>
                <div className="me-header-inner">
                    <RestaurantLogo src={restaurant.logoUrl} className="me-logo" />
                    <div>
                        <h1 className="me-title" title={restaurant.name}>{restaurant.name || 'Restaurant'}</h1>
                        <p className="me-sub" title={restaurant.description}>{restaurant.description || 'Welcome to our restaurant'}</p>
                    </div>
                </div>
            </header>

            {/* Search bar */}
            <div className="search-field with-icon">
                <span className="search-icon" aria-hidden="true">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" focusable="false" aria-hidden="true">
                        <circle cx="11" cy="11" r="7" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                </span>
                <input
                    type="search"
                    placeholder="Search menus, categories, or items..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    aria-label="Search menus and items"
                />
            </div>

            {/* Show search results when query present */}
            {searchQuery && (
                <main className="me-main">
                    <section className="me-section">
                        <h2 className="me-section-title">Search Results</h2>

                        {searchResults.menus.length === 0 && searchResults.categories.length === 0 && searchResults.items.length === 0 && (
                            <div className="me-no-results">No results found for "{searchQuery}"</div>
                        )}

                        {searchResults.menus.length > 0 && (
                            <div className="me-search-group">
                                <h3>Menus</h3>
                                <div className="me-grid">
                                    {searchResults.menus.map(m => (
                                        <div key={m.id} className="me-card" onClick={() => { setSearchQuery(''); openMenu(m); }}>
                                            <div className="me-card-content">
                                                <h3 className="me-card-title" title={m.name}>{m.name}</h3>
                                                <p className="me-card-sub" title={m.description}>{m.description || 'Discover our selection'}</p>
                                            </div>
                                            <div className="me-card-arrow">→</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {searchResults.categories.length > 0 && (
                            <div className="me-search-group">
                                <h3>Categories</h3>
                                <div className="me-grid">
                                    {searchResults.categories.map(({ menu, category }) => (
                                        <div key={category.id} className="me-card" onClick={() => { setSearchQuery(''); openMenu(menu); setTimeout(() => openCategory(category), 50); }}>
                                            <div className="me-card-content">
                                                <h3 className="me-card-title" title={category.name}>{category.name}</h3>
                                                <p className="me-card-sub" title={category.description}>{category.description || `${(category.items || []).length} items available`}</p>
                                            </div>
                                            <div className="me-card-arrow">→</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {searchResults.items.length > 0 && (
                            <div className="me-search-group">
                                <h3>Items</h3>
                                <div className="me-items-grid">
                                    {searchResults.items.map(({ menu, category, item }) => (
                                        <div key={item.id} className={`me-item-card ${!item.isAvailable ? 'me-unavailable' : ''}`}>
                                            <div className="me-item-main" onClick={() => { openMenu(menu); setTimeout(() => { setActiveCategory(category); openItem(item); }, 50); }}>
                                                <div className="me-item-left">
                                                    <MenuItemImage src={item.imageUrl || DEFAULT_ITEM} alt={item.name} />
                                                </div>
                                                <div className="me-item-right">
                                                    <div className="me-item-title-row">
                                                        {(item.isVeg !== undefined && item.isVeg !== null) && (
                                                            <span className={`me-veg-badge ${item.isVeg ? 'veg' : 'nonveg'}`}>
                                                                <span className="me-veg-dot"></span>
                                                            </span>
                                                        )}
                                                        <h4 className="me-item-name-text" title={item.name}>{item.name}</h4>
                                                    </div>
                                                    <p className="me-item-desc" title={item.description}>{item.description}</p>

                                                    <div className="me-item-meta">
                                                        {Number(item.price) > 0 && (
                                                            <span className="me-price">₹{Number(item.price).toLocaleString()}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                    </section>
                </main>
            )}

            {/* Main content area */}
            {/* Offers carousel/banner */}
            {!searchQuery && bannerOffers && bannerOffers.length > 0 && (
                <div
                    className={`me-offers-carousel ${isDragging ? 'me-dragging' : ''}`}
                    aria-roledescription="carousel"
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                    onMouseDown={onMouseDown}
                    onMouseMove={onMouseMove}
                    onMouseUp={onMouseUp}
                    onMouseLeave={onMouseLeave}
                    onMouseEnter={() => setIsPaused(true)}
                    style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                >
                    {bannerOffers.map((b, idx) => (
                        <div key={b.id || idx} className={`me-offer-slide ${idx === bannerIndex ? 'active' : ''}`}>
                            <div style={{ backgroundImage: b.imageUrl || b.image ? `url(${b.imageUrl || b.image})` : 'none' }}>
                                <div className="me-offer-overlay">
                                    {b.title && <div className="me-offer-title">{b.title}</div>}
                                    {b.description && <div className="me-offer-desc">{b.description}</div>}
                                    {b.ctaText && b.ctaUrl && (
                                        <a className="me-offer-cta" href={b.ctaUrl} target="_blank" rel="noreferrer">{b.ctaText}</a>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                    {bannerOffers.length > 1 && (
                        <>
                            <div className="me-offers-dots">
                                {bannerOffers.map((_, i) => (
                                    <button
                                        key={i}
                                        className={`me-dot ${i === bannerIndex ? 'active' : ''}`}
                                        onClick={() => {
                                            setBannerIndex(i);
                                            setIsPaused(true);
                                            setTimeout(() => setIsPaused(false), 3000);
                                        }}
                                        aria-label={`Go to slide ${i + 1}`}
                                    />
                                ))}
                            </div>
                            {/* Swipe hint indicators */}
                            <div className="me-swipe-hints">
                                <div className="me-swipe-hint me-swipe-left" aria-hidden="true">‹</div>
                                <div className="me-swipe-hint me-swipe-right" aria-hidden="true">›</div>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* Floating Notes Icon Button */}
            {notesOffers && notesOffers.length > 0 && (
                <button
                    className="me-notes-fab"
                    onClick={() => setShowNotesModal(true)}
                    aria-label="View important notes"
                    title="Important Notes"
                >
                    <span className="me-notes-icon">📌</span>
                    <span className="me-notes-badge">{notesOffers.length}</span>
                </button>
            )}

            {!searchQuery && (
                <main className="me-main">
                    {/* === Menus List === */}
                    {stage === 'menus' && (
                        <section className="me-section">
                            <h2 className="me-section-title">Explore Our Menus</h2>
                            <div className="me-grid">
                                {menus.map(menu => (
                                    <div key={menu.id} className="me-card" onClick={() => openMenu(menu)}>
                                        <div className="me-card-content">
                                            <h3 className="me-card-title" title={menu.name}>{menu.name}</h3>
                                            <p className="me-card-sub" title={menu.description}>{menu.description || 'Discover our selection'}</p>
                                        </div>
                                        <div className="me-card-arrow">→</div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* === Categories List === */}
                    {stage === 'categories' && activeMenu && (
                        <section className="me-section">
                            {!isSingleMenu && (
                                <div className="me-back" onClick={goBack}>
                                    <span>←</span> All Menus
                                </div>
                            )}
                            <h2 className="me-section-title">{activeMenu.name} Categories</h2>
                            <div className="me-grid">
                                {(activeMenu.categories || []).sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)).map(cat => (
                                    <div key={cat.id} className="me-card" onClick={() => openCategory(cat)}>
                                        <div className="me-card-content">
                                            <h3 className="me-card-title" title={cat.name}>{cat.name}</h3>
                                            <p className="me-card-sub" title={cat.description}>{cat.description || `${(cat.items || []).length} items available`}</p>
                                        </div>
                                        <div className="me-card-arrow">→</div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* === Items List === */}
                    {stage === 'items' && activeMenu && activeCategory && (
                        <section className="me-section">
                            <div className="me-back" onClick={goBack}>
                                <span>←</span> Back to Categories
                            </div>
                            <h2 className="me-section-title">{activeCategory.name}</h2>
                            <div className="me-items-grid">
                                {(activeCategory.items || []).map(item => (
                                    <div key={item.id} className={`me-item-card ${!item.isAvailable ? 'me-unavailable' : ''}`}>
                                        <div className="me-item-main" onClick={() => openItem(item)}>
                                            <div className="me-item-left">
                                                <MenuItemImage src={item.imageUrl || DEFAULT_ITEM} alt={item.name} />
                                            </div>
                                            <div className="me-item-right">
                                                <div className="me-item-title-row">
                                                    {(item.isVeg !== undefined && item.isVeg !== null) && (
                                                        <span className={`me-veg-badge ${item.isVeg ? 'veg' : 'nonveg'}`}>
                                                            <span className="me-veg-dot"></span>
                                                        </span>
                                                    )}
                                                    <h4 className="me-item-name-text" title={item.name}>{item.name}</h4>
                                                </div>
                                                <p className="me-item-desc" title={item.description}>{item.description}</p>

                                                <div className="me-item-meta">
                                                    {Number(item.price) > 0 && (
                                                        <span className="me-price">₹{Number(item.price).toLocaleString()}</span>
                                                    )}
                                                </div>

                                                {item.tags && item.tags.length > 0 && (
                                                    <div className="me-badges">
                                                        {item.tags.map((t, i) => (
                                                            <span key={i} className="me-tag-pill">{t}</span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}
                </main>
            )}

            <Footer />

            {/* Notes Modal */}
            {showNotesModal && notesOffers && notesOffers.length > 0 && (
                <Modal onClose={() => { setShowNotesModal(false); setCurrentNoteIndex(0); setIsNotesPaused(false); }}>
                    <h3 className="me-modal-title">📌 Important Notes</h3>
                    <div
                        className="me-notes-carousel-container"
                        onTouchStart={onNotesTouchStart}
                        onTouchMove={onNotesTouchMove}
                        onTouchEnd={onNotesTouchEnd}
                        onMouseDown={onNotesMouseDown}
                        onMouseMove={onNotesMouseMove}
                        onMouseUp={onNotesMouseUp}
                        onMouseLeave={onNotesMouseLeave}
                        style={{ cursor: isNotesDragging ? 'grabbing' : 'grab' }}
                    >
                        <div className="me-notes-carousel-track">
                            {notesOffers.map((n, i) => (
                                <div key={n.id || i} className={`me-note-carousel-card ${i === currentNoteIndex ? 'active' : ''}`}>
                                    {n.title && <div className="me-note-modal-title">{n.title}</div>}
                                    {n.description && (
                                        <div className="me-note-modal-desc">
                                            {formatNoteText(n.description)}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                        {notesOffers.length > 1 && (
                            <>
                                {/* Swipe hint text */}
                                <div className="me-swipe-hint">← Swipe →</div>
                                {/* Dots indicator */}
                                <div className="me-notes-dots">
                                    {notesOffers.map((_, i) => (
                                        <div
                                            key={i}
                                            className={`me-note-dot ${i === currentNoteIndex ? 'active' : ''}`}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </Modal>
            )}

            {/* Variants Modal */}
            {showVariantsModal && activeItem && (
                <Modal onClose={goBack}>
                    <div className="me-modal-item-header">
                        <div className="me-modal-item-image">
                            <MenuItemImage src={activeItem.imageUrl || DEFAULT_ITEM} alt={activeItem.name} />
                        </div>
                        <div className="me-modal-item-info">
                            <div className="me-item-title-row">
                                {(activeItem.isVeg !== undefined && activeItem.isVeg !== null) && (
                                    <span className={`me-veg-badge ${activeItem.isVeg ? 'veg' : 'nonveg'}`}>
                                        <span className="me-veg-dot"></span>
                                    </span>
                                )}
                                <h4 className="me-item-name-text" title={activeItem.name}>{activeItem.name}</h4>
                            </div>
                            <p title={activeItem.description}>{activeItem.description || 'Customizations for this item'}</p>
                        </div>
                    </div>

                    <div className="me-variants-list">
                        {(activeItem.variants || []).filter(v => v.isActive).map(v => (
                            <div key={v.id} className="me-variant-row">
                                <div className="me-variant-label">{v.name}</div>
                                <div className="me-variant-price">
                                    {Number(v.price) > 0 ? `₹${Number(v.price).toLocaleString()}` : (Number(activeItem.price) > 0 ? `₹${Number(activeItem.price).toLocaleString()}` : '')}
                                </div>
                            </div>
                        ))}
                    </div>
                </Modal>
            )}
        </div>
    );
};

const Modal = ({ children, onClose }) => (
    <div className="me-modal-overlay" onClick={onClose}>
        <div className="me-modal" onClick={(e) => e.stopPropagation()}>
            <button className="me-modal-close" onClick={onClose}>✕</button>
            <div className="me-modal-body">{children}</div>
        </div>
    </div>
);

export default MenuExplorer;
